/** @author PVCOMBANK */
import * as React from "react";
import { Paragraph, useTheme } from "react-native-paper";
import { RegisterOptions, useForm } from "react-hook-form";
import { Button } from "react-native-paper";
import { Block } from "@block";
import { useDesktopMediaQuery } from "@media-query";
import { useTranslation } from "react-i18next";
import { spacing } from "@styles/spacing";
import { QueueSpacing, StackSpacing } from "@components/spacing";
import { InputText, InputAutoComplete, InputTextMultiline } from "app/shared/components/form/input";
import { StyleSheet } from "react-native";
import { ACCOUNT } from "app/shared/services/services-model.type";
import { DataAutoBillingCreate } from "../../reducer/types";
import { ItemCompleteProps } from "app/shared/components/modal/auto-complete/auto-complete";
import {
  handlerAutoBillingPaymentType,
  handlerAutoBillingProviders,
  handlerAutoBillingServices,
  loadAutoBillingServiceJson,
} from "app/modules/auto-billings/auto-billing-utils/auto-billing.utils";
import { ServiceCode } from "app/shared/services/api-path";

export interface FormAutoBillingProps {
  onSubmit: Function;
  accountDebit: ACCOUNT;
  onCancel: Function;
  requestCreate: Function;
  showModal: Function;
  closeModal: Function;
}

enum FormEnum {
  serviceType,
  serviceName,
  providerID,
  providerName,
  customerCode,
  paymentType,
  paymentTypeName,
  paymentDate,
  note,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export type FormAutoBillingData = {
  serviceType: string;
  serviceName: string;
  providerID: string;
  providerName: string;
  customerCode: string;
  paymentType: string;
  paymentTypeName: string;
  paymentDate: string;
  note: string;
};

const FormAutoBilling = (props: FormAutoBillingProps) => {
  const { onSubmit, requestCreate, onCancel, accountDebit } = props;
  const { colors } = useTheme();
  const { t } = useTranslation();
  const isDesktop = useDesktopMediaQuery();

  // Instance Form Method
  const { control, handleSubmit, getValues, setValue, trigger, clearErrors } = useForm<FormAutoBillingData>({
    defaultValues: {
      serviceType: "",
      serviceName: "",
      providerID: "",
      providerName: "",
      customerCode: "",
      paymentType: "",
      paymentTypeName: "",
      paymentDate: "",
      note: "",
    },
    mode: "onChange",
  });

  const [serviceArray, setServiceArray] = React.useState<ItemCompleteProps[]>([]);
  const [providerArray, setProviderArray] = React.useState<ItemCompleteProps[]>([]);
  const [paymentTypeArray, setPaymentTypeArray] = React.useState<ItemCompleteProps[]>([]);
  const [paymentDateArray, setPaymentDateArray] = React.useState<ItemCompleteProps[]>([]);
  const [showChooseDate, setShowChooseDate] = React.useState<boolean>(false);

  // Styles Component
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        fieldFromText: {
          color: colors.text,
          width: 200,
          fontWeight: "700",
          fontSize: 16,
        },
        buttonContainer: { width: 128, alignSelf: "center", backgroundColor: colors.notification },
        acceptButtonContainer: { width: 128, alignSelf: "center" },
      }),
    [colors.text, colors.notification],
  );

  // Tạo validate Schema
  const validateSchemeForm: ValidDataSchemeFormData = {
    serviceType: {
      required: {
        value: true,
        message: t("BILLING_VUI_LONG_CHON_DICH_VU"),
      },
    },
    serviceName: {
      required: {
        value: false,
        message: t("BILLING_VUI_LONG_CHON_DICH_VU"),
      },
    },
    providerID: {
      required: {
        value: true,
        message: t("AUTO_BILLING_VUI_LONG_NHAP_NHA_CUNG_CAP"),
      },
    },
    providerName: {
      required: {
        value: false,
        message: t("AUTO_BILLING_VUI_LONG_NHAP_NHA_CUNG_CAP"),
      },
    },
    customerCode: {
      required: {
        value: true,
        message: t("AUTO_BILLING_VUI_LONG_NHAP_MA_HOA_DON"),
      },
    },
    paymentType: {
      required: {
        value: true,
        message: t("AUTO_BILLING_VUI_LONG_NHAP_MA_HOA_DON"),
      },
    },
    paymentTypeName: {
      required: {
        value: false,
        message: t("BILLING_VUI_LONG_NHAP_MA_HOA_DON"),
      },
    },
    paymentDate: {
      required: {
        value: showChooseDate,
        message: t("AUTO_BILLING_VUI_LONG_CHON_NGAY_THANH_TOAN"),
      },
    },
    note: {
      required: {
        value: false,
        message: "",
      },
    },
  };

  const handleRequestCreate = (value: FormAutoBillingData): void => {
    const dataUpdate: DataAutoBillingCreate = {
      ACCOUNT_DEBIT: accountDebit,
      SERVICE: { value: value.serviceType, label: value.serviceName },
      PROVIDER: { value: value.providerID, label: value.providerName },
      SERVICE_CODE: ServiceCode.AUTO_BILLING,
      INQUIRY_CODE: value.customerCode,
      PAYMENT_DATE: { value: value.paymentType, label: value.paymentTypeName, other: value.paymentDate },
      NOTE: value.note,
    };
    requestCreate(dataUpdate);
  };

  /// MARK: - Action Handler
  const onPressCreate = () => {
    onSubmit();
    handleSubmit(handleRequestCreate).call(() => {});
  };

  const onClickProvider = async () => {
    trigger("serviceType");
  };

  const checkSelectProvider = () => {
    if (getValues("serviceType").length) return false;
    return true;
  };

  /// MARK: - Bussiness Logic HERE
  const onSelectedService = (service?: ItemCompleteProps) => {
    if (providerArray) setProviderArray([]);
    console.log("onSelectedService: ", service);
    setValue("serviceName", service?.label || "");
    clearErrors("customerCode");
    setValue("customerCode", "");
    clearErrors("customerCode");
    setValue("providerID", "");
    setValue("providerName", "");
    clearErrors("providerID");
    setTimeout(() => {
      const listProvider = handlerAutoBillingProviders(service?.value);
      setProviderArray(listProvider);
    }, 100);
  };

  const onSelectedProvider = (provider?: ItemCompleteProps) => {
    setValue("providerName", provider?.label || "");
    setValue("customerCode", "");
    clearErrors("customerCode");
  };

  const onSelectedPaymentType = (_item?: ItemCompleteProps) => {
    setValue("paymentTypeName", _item?.label || "");
    setValue("paymentDate", "");
    clearErrors("paymentDate");
    if (!showChooseDate && _item?.value === "N") setShowChooseDate(true);
    else if (showChooseDate && _item?.value !== "N") setShowChooseDate(false);
  };

  // set thông tin billing
  const onBlurTopUpCustomer = async () => {
    trigger("customerCode");
  };

  /// MARK: - Child Component
  const FieldForm = (
    <Block>
      <StackSpacing size="medium" />
      <Paragraph style={styles.fieldFromText}>{t("TRANFER_TAI_KHOAN_NHAN")}</Paragraph>
      <StackSpacing size="medium" />
    </Block>
  );

  const FormInput = (
    <Block flex={1} bg={colors.backgroundForm} br={12} p={spacing.large}>
      <InputAutoComplete
        rules={validateSchemeForm.serviceType}
        name="serviceType"
        options={serviceArray}
        control={control}
        title={t("AUTO_BILLING_DICH_VU")}
        label={t("TOPUP_CHON_DICH_VU")}
        onDismiss={onSelectedService}
        showSearch={true}
        showIcon={true}
      />
      <InputAutoComplete
        rules={validateSchemeForm.providerID}
        name="providerID"
        options={providerArray}
        control={control}
        title={t("AUTO_BILLING_NHA_CUNG_CAP")}
        label={t("TOPUP_CHON_NHA_CUNG_CAP")}
        onClick={onClickProvider}
        checkSelect={checkSelectProvider()}
        onDismiss={onSelectedProvider}
      />
      <InputText
        name="customerCode"
        control={control}
        rules={validateSchemeForm.customerCode}
        label={t("BILLING_MA_KHACH_HANG")}
        note={t("BILLING_MA_HOA_DON_KHACH_HANG")}
        onBlur={onBlurTopUpCustomer}
      />
      <InputAutoComplete
        rules={validateSchemeForm.paymentType}
        name="paymentType"
        options={paymentTypeArray}
        control={control}
        title={t("AUTO_BILLING_PHUONG_THUC_THANH_TOAN")}
        label={t("AUTO_BILLING_CHON_PHUONG_THUC_THANH_TOAN")}
        onDismiss={onSelectedPaymentType}
      />
      <InputAutoComplete
        rules={validateSchemeForm.paymentDate}
        name="paymentDate"
        options={paymentDateArray}
        control={control}
        title={t("AUTO_BILLING_NGAY_THANH_TOAN")}
        label={t("AUTO_BILLING_CHON_NGAY_THANH_TOAN")}
        isShow={showChooseDate}
      />
      <InputTextMultiline
        inputProps={{ multiline: true, maxLength: 100 }}
        name="note"
        control={control}
        rules={validateSchemeForm.note}
        label={t("AUTO_BILLING_GHI_CHU")}
      />
    </Block>
  );

  const ButtonForm = (
    <Block row middle pv={spacing.huge}>
      <Block w={isDesktop ? 200 : 0} />
      <Button onPress={() => onCancel()} mode="contained" style={styles.buttonContainer}>
        {t("COMMON_HUY")}
      </Button>
      <QueueSpacing size="large" />
      <Button mode="contained" style={styles.acceptButtonContainer} onPress={onPressCreate}>
        {t("COMMON_TIEP_TUC")}
      </Button>
    </Block>
  );

  React.useEffect(() => {
    loadAutoBillingServiceJson().then(() => {
      const listService = handlerAutoBillingServices();
      setServiceArray(listService);
    });
    const listType = handlerAutoBillingPaymentType();
    setPaymentTypeArray(listType);
  }, []);

  React.useEffect(() => {
    let dateArray: ItemCompleteProps[] = [];
    for (let i: number = 1; i < 29; i++) {
      const _item: ItemCompleteProps = {
        value: String(i),
        label: i < 10 ? "0" + String(i) : String(i),
      };
      dateArray.push(_item);
    }
    console.log("dateArray: ", dateArray);
    setPaymentDateArray(dateArray);
  }, [t]);

  return (
    <>
      <Block row={isDesktop ? true : false} pt={spacing.medium}>
        {FieldForm}
        {FormInput}
      </Block>
      {ButtonForm}
    </>
  );
};

export default FormAutoBilling;
